import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { AuthProvider } from './context/AuthContext';
import { BackgroundPreloader } from './components/BackgroundPreloader';
import './index.css';

// Lazy load pages for better performance
const LoginPage = React.lazy(() => import('./pages/LoginPage'));
const UsersPage = React.lazy(() => import('./pages/UsersPage'));
const OffersPage = React.lazy(() => import('./pages/OffersPage'));
const AccountsPage = React.lazy(() => import('./pages/AccountsPage'));
const TransactionsPage = React.lazy(() => import('./pages/TransactionsPage'));

// Layout components
const Layout = React.lazy(() => import('./components/Layout'));
const AuthGuard = React.lazy(() => import('./components/auth/AuthGuard'));

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes
    },
  },
});

// Wrapper component for protected routes
const ProtectedPage = ({ children }: { children: React.ReactNode }) => {
  return (
    <React.Suspense fallback={
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
      </div>
    }>
      <AuthGuard>
        <Layout>
          {children}
        </Layout>
      </AuthGuard>
    </React.Suspense>
  );
};

const App: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <BackgroundPreloader />
        <Router>
          <React.Suspense fallback={
            <div className="flex items-center justify-center h-screen">
              <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
            </div>
          }>
            <Routes>
              {/* Public route */}
              <Route path="/login" element={<LoginPage />} />
              
              {/* Protected routes */}
              <Route path="/" element={<Navigate to="/users" replace />} />
              <Route path="/users" element={<ProtectedPage><UsersPage /></ProtectedPage>} />
              <Route path="/offers" element={<ProtectedPage><OffersPage /></ProtectedPage>} />
              <Route path="/accounts" element={<ProtectedPage><AccountsPage /></ProtectedPage>} />
              <Route path="/transactions" element={<ProtectedPage><TransactionsPage /></ProtectedPage>} />
              
              {/* Catch all route */}
              <Route path="*" element={<Navigate to="/users" replace />} />
            </Routes>
          </React.Suspense>
        </Router>
      </AuthProvider>
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  );
};

export default App;
